import { registerMockAPI } from '~/mocks/mockApis';

import { keepPreviousData, skipToken, useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { deliveryAgencyKeys } from './keys';

import type {
  GetCollectingRequestCandidatesParams,
  GetCollectingRequestCandidatesResponse,
} from '~/types/delivery-agency.types';

export const fetchCollectingRequestCandidates = async (
  params: GetCollectingRequestCandidatesParams
): Promise<GetCollectingRequestCandidatesResponse> => {
  const { data } = await axiosInstance.get<GetCollectingRequestCandidatesResponse>(
    API_ENDPOINT.DELIVERY_AGENCY.GET_COLLECTING_REQUEST_CANDIDATES,
    { params }
  );
  return data;
};

type UseGetCollectingRequestCandidatesQueryOptions = {
  params: GetCollectingRequestCandidatesParams | null;
  showGlobalDimOverlay?: boolean;
};

export const useGetCollectingRequestCandidatesQuery = ({
  params,
  showGlobalDimOverlay = true,
}: UseGetCollectingRequestCandidatesQueryOptions) => {
  return useQuery({
    queryKey: deliveryAgencyKeys.collectingRequestCandidates(params),
    queryFn: params ? () => fetchCollectingRequestCandidates(params) : skipToken,
    staleTime: 0,
    gcTime: 1000 * 30,
    // 조회 조건이 없을 때(skipToken)도 placeholder가 적용돼 직전 목록이 남으므로, 검색 중일 때만 이어 보여준다
    placeholderData: params ? keepPreviousData : undefined,
    meta: { showGlobalDimOverlay },
  });
};

registerMockAPI<GetCollectingRequestCandidatesResponse>({
  method: 'get',
  endpoint: API_ENDPOINT.DELIVERY_AGENCY.GET_COLLECTING_REQUEST_CANDIDATES,
  label: '배송 주문 반품 신청·조회/반품 신청 후보 목록',
  scenarios: [
    {
      name: '정상 응답 (3건)',
      response: {
        data: [
          {
            outboundId: 90001,
            outboundCode: 'OB20260803001',
            clientOrderCodes: ['ORD-20260803-0001'],
            receiverName: '홍길동',
            ordererName: '홍길동',
            productNames: ['유리컵 2개'],
            estimatedDeliveryDate: '2026-08-04',
          },
          {
            outboundId: 90002,
            outboundCode: 'OB20260803002',
            clientOrderCodes: ['ORD-20260803-0002', 'ORD-20260803-0003'],
            receiverName: '김철수',
            ordererName: '김영희',
            productNames: ['전기포트', '커피 필터 100매'],
            estimatedDeliveryDate: '2026-08-04',
          },
          {
            outboundId: 90003,
            outboundCode: 'OB20260802004',
            clientOrderCodes: [],
            receiverName: '이순신',
            ordererName: '이순신',
            productNames: ['접시 세트'],
            estimatedDeliveryDate: '2026-08-03',
          },
        ],
        pagination: { cursor: 1, listView: 50, listTotal: 3 },
      },
    },
    {
      name: '1건만 (단일 선택 확인)',
      response: {
        data: [
          {
            outboundId: 90010,
            outboundCode: 'OB20260801010',
            clientOrderCodes: ['ORD-20260801-0010'],
            receiverName: '박보검',
            ordererName: '박보검',
            productNames: ['텀블러'],
            estimatedDeliveryDate: '2026-08-02',
          },
        ],
        pagination: { cursor: 1, listView: 50, listTotal: 1 },
      },
    },
    {
      name: '여러 페이지 (총 120건 중 1쪽)',
      response: {
        data: Array.from({ length: 50 }, (_, index) => ({
          outboundId: 91000 + index,
          outboundCode: `OB2026080${(index % 9) + 1}${String(index).padStart(3, '0')}`,
          clientOrderCodes: [`ORD-20260801-${String(index).padStart(4, '0')}`],
          receiverName: `수령자${index + 1}`,
          ordererName: `주문자${index + 1}`,
          productNames: [`상품 ${index + 1}`],
          estimatedDeliveryDate: '2026-08-03',
        })),
        pagination: { cursor: 1, listView: 50, listTotal: 120 },
      },
    },
    {
      name: '검색 결과 없음',
      response: { data: [], pagination: { cursor: 1, listView: 50, listTotal: 0 } },
    },
    {
      name: '서버 에러 (500)',
      status: 500,
      response: { data: [], pagination: { cursor: 1, listView: 50, listTotal: 0 } },
    },
  ],
});
